import React, { useEffect, useState } from 'react';
import { FormGroup, FormControlLabel } from '@mui/material';
import MuiSwitch from '../../shared/components/MuiSwitch';
import GET_USER_EMAIL_PREFERENCES from '../queries/getUserEmailPreferences';
import UPDATE_USER_EMAIL_PREFERENCES from '../queries/updateUserEmailPreferences';
import ApolloError from './../../shared/components/ApolloError';
import { useQuery, useMutation } from '@apollo/client';
import PageInstructions from '../../shared/components/PageInstructions';

const EmailPreferences = () => {
  const [preferences, setPreferences] = useState({
    AppraisalDataAlerts: true,
    CheckPointDataAlerts: true,
    ReportManagementFdaAlerts: true,
    ReportManagementOrganizationAlerts: true,
    UserManagementAlerts: true
  });

  const [preferencesAccessibilities, setPreferencesAccessibilities] = useState({
    AppraisalDataAlerts: false,
    CheckPointDataAlerts: false,
    ReportManagementFdaAlerts: false,
    ReportManagementOrganizationAlerts: false,
    UserManagementAlerts: false
  });

  const title = 'Update Email Preferences';
  const description =
    'Update email preferences by moving the toggle to Opt-In or Opt-Out of each email alert category below.';

  const {
    data: userEmailPreferencesData,
    error: userEmailPreferencesError,
    loading: userEmailPreferencesLoading
  } = useQuery(GET_USER_EMAIL_PREFERENCES);

  const [updateUserEmailPreferences, { error: updateError }] = useMutation(
    UPDATE_USER_EMAIL_PREFERENCES,
    {
      update: cache => {
        cache.evict({
          query: GET_USER_EMAIL_PREFERENCES
        });
      }
    }
  );

  useEffect(() => {
    if (!userEmailPreferencesData) {
      return;
    }

    if (userEmailPreferencesData.userEmailPreferences) {
      setPreferences(userEmailPreferencesData.userEmailPreferences);
    }

    if (userEmailPreferencesData.userEmailPreferencesAccessibilities) {
      setPreferencesAccessibilities(userEmailPreferencesData.userEmailPreferencesAccessibilities);
    }
  }, [userEmailPreferencesData]);

  const handleSettingChange = async (checked, field) => {
    const updatedEmailData = { [`${field}`]: checked };
    setPreferences({ ...preferences, ...updatedEmailData });
    updateUserEmailPreferences({ variables: { data: updatedEmailData } });
  };

  if (userEmailPreferencesError || updateError) {
    return <ApolloError />;
  }

  return (
    <div className='email-preferences '>
      <PageInstructions title={title} description={description} />
      <div className='settings-root'>
        <FormGroup className='settings-container' data-testid='settings-container'>
          {preferencesAccessibilities.AppraisalDataAlerts && (
            <FormControlLabel
              key='AppraisalDataAlerts'
              labelPlacement='start'
              label='Appraisal Data Alerts'
              className='setting-label'
              control={
                <MuiSwitch
                  data-testid='setting-input-AppraisalDataAlerts'
                  checked={preferences.AppraisalDataAlerts}
                  onChange={(_, checked) => handleSettingChange(checked, 'AppraisalDataAlerts')}
                  loading={userEmailPreferencesLoading}
                />
              }
            />
          )}
          {preferencesAccessibilities.CheckPointDataAlerts && (
            <FormControlLabel
              key='CheckPointDataAlerts'
              labelPlacement='start'
              label='Check Point Data Alerts'
              className='setting-label'
              control={
                <MuiSwitch
                  data-testid='setting-input-CheckPointDataAlerts'
                  checked={preferences.CheckPointDataAlerts}
                  onChange={(_, checked) => handleSettingChange(checked, 'CheckPointDataAlerts')}
                  loading={userEmailPreferencesLoading}
                />
              }
            />
          )}
          {preferencesAccessibilities.ReportManagementFdaAlerts && (
            <FormControlLabel
              key='ReportManagementFdaAlerts'
              labelPlacement='start'
              label='Report Management - FDA Alerts'
              className='setting-label'
              control={
                <MuiSwitch
                  data-testid='setting-input-ReportManagementFdaAlerts'
                  checked={preferences.ReportManagementFdaAlerts}
                  onChange={(_, checked) =>
                    handleSettingChange(checked, 'ReportManagementFdaAlerts')
                  }
                  loading={userEmailPreferencesLoading}
                />
              }
            />
          )}
          {preferencesAccessibilities.ReportManagementOrganizationAlerts && (
            <FormControlLabel
              key='ReportManagementOrganizationAlerts'
              labelPlacement='start'
              label='Report Management - Organization Alerts'
              className='setting-label'
              control={
                <MuiSwitch
                  data-testid='setting-input-ReportManagementOrganizationAlerts'
                  checked={preferences.ReportManagementOrganizationAlerts}
                  onChange={(_, checked) =>
                    handleSettingChange(checked, 'ReportManagementOrganizationAlerts')
                  }
                  loading={userEmailPreferencesLoading}
                />
              }
            />
          )}
          {preferencesAccessibilities.UserManagementAlerts && (
            <FormControlLabel
              key='UserManagementAlerts'
              labelPlacement='start'
              label='User Management Alerts'
              className='setting-label'
              control={
                <MuiSwitch
                  data-testid='setting-input-UserManagementAlerts'
                  checked={preferences.UserManagementAlerts}
                  onChange={(_, checked) => handleSettingChange(checked, 'UserManagementAlerts')}
                  loading={userEmailPreferencesLoading}
                />
              }
            />
          )}
        </FormGroup>
      </div>
    </div>
  );
};

export default EmailPreferences;
